/* =========================================================
   static/table_engine_src/plugins/io.js — CSV/JSON import/export for TableEngine

   - Uses spec.import aliases (header-based, order independent)
   - Export reads inputs via eng.readRow() and outputs via eng.getOutputRaw()/data-raw
   - Designed to work with "scale" in TableEngine:
       * eng.readRow() should return CANONICAL (unscaled) values
       * CSV export defaults to CANONICAL too
   - Allows per-spec hooks: spec.io = { sanitizeRow, filterRow, buildExportRow, buildImportRow }
   - Optional import conflict resolution for merge/append with row keys:
       * opts.mode = "merge" (or "append" + conflict policy)
       * opts.conflictKey (fallback: spec.rowKey)
       * opts.resolveConflict/conflictAssistant/conflictAction

   IMPORT VALIDATION:
   - Aborts import if the file does not match table structure.
   - Uses spec.io.importValidation or opts.importValidation:
       {
         mode: "strict" | "warn" | "off",   // default: "strict"
         requiredKeys: string[],            // default: [] (no required)
         minMatched: number                 // default: 1 (at least one known column must match)
       }
   ========================================================= */

"use strict";

import { parseNumber } from "../format/format_numbers.js";

const IO = {};

// -------------------------
// Small helpers
// -------------------------
const DEFAULT_CSV_FORMULA_PATTERN = /^[\t\r ]*[=+\-@]/;

function resolveCsvOptions(opts = {}) {
  const csv = opts && typeof opts.csv === "object" ? opts.csv : null;
  const formulaPattern =
    csv?.formulaPattern instanceof RegExp
      ? csv.formulaPattern
      : opts.formulaPattern instanceof RegExp
        ? opts.formulaPattern
        : DEFAULT_CSV_FORMULA_PATTERN;
  return {
    neutralizeFormulas:
      (csv?.neutralizeFormulas ?? opts.neutralizeFormulas ?? true) !== false,
    formulaPrefix: String(csv?.formulaPrefix ?? opts.formulaPrefix ?? "'"),
    formulaPattern,
    stripNullBytes: (csv?.stripNullBytes ?? opts.stripNullBytes ?? true) !== false,
  };
}

function sanitizeCsvCellValue(value, csvOptions) {
  if (value == null) return "";

  if (typeof value !== "string") return value;

  let out = value;
  if (csvOptions.stripNullBytes) out = out.replace(/\0/g, "");

  if (csvOptions.neutralizeFormulas) {
    const pattern = csvOptions.formulaPattern instanceof RegExp
      ? csvOptions.formulaPattern
      : DEFAULT_CSV_FORMULA_PATTERN;
    pattern.lastIndex = 0;
    if (pattern.test(out)) out = `${csvOptions.formulaPrefix}${out}`;
  }

  return out;
}

function csvEscape(x, csvOptions = resolveCsvOptions()) {
  const safeValue = sanitizeCsvCellValue(x, csvOptions);
  const s = String(safeValue ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function safeNumber(x, def = NaN) {
  const n = parseNumber(x, NaN);
  return Number.isFinite(n) ? n : def;
}

function normalizeLimit(value, fallback) {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (n <= 0) return Infinity;
  return Math.max(1, Math.floor(n));
}

function getImportLimits(spec, opts = {}) {
  const io = spec?.io && typeof spec.io === "object" ? spec.io : null;
  const base = (opts.importLimits && typeof opts.importLimits === "object"
    ? opts.importLimits
    : io?.importLimits && typeof io.importLimits === "object"
      ? io.importLimits
      : {}) || {};

  return {
    maxBytes: normalizeLimit(opts.maxImportBytes ?? base.maxBytes, 50 * 1024 * 1024),
    maxRows: normalizeLimit(opts.maxImportRows ?? base.maxRows, 250000),
    maxColumns: normalizeLimit(opts.maxImportColumns ?? base.maxColumns, 2048),
    maxCellChars: normalizeLimit(opts.maxImportCellChars ?? base.maxCellChars, 500000),
  };
}

function parseCSV(text, limits = {}) {
  const src = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/^\uFEFF/, ""); // Strip UTF-8 BOM if present (Excel)

  if (!src.trim()) return { header: [], rows: [] };

  if (Number.isFinite(limits.maxBytes) && src.length > limits.maxBytes * 2) {
    throw new Error(
      `Import aborted: CSV text exceeds configured size limit (${limits.maxBytes} bytes).`,
    );
  }

  const out = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        // Escaped quote inside quoted field
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
        if (Number.isFinite(limits.maxCellChars) && field.length > limits.maxCellChars) {
          throw new Error(
            `Import aborted: CSV cell exceeds configured max length (${limits.maxCellChars}).`,
          );
        }
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ",") {
      row.push(field.trim());
      if (Number.isFinite(limits.maxColumns) && row.length > limits.maxColumns) {
        throw new Error(
          `Import aborted: CSV row exceeds configured max columns (${limits.maxColumns}).`,
        );
      }
      field = "";
      continue;
    }
    if (ch === "\n") {
      row.push(field.trim());
      if (Number.isFinite(limits.maxColumns) && row.length > limits.maxColumns) {
        throw new Error(
          `Import aborted: CSV row exceeds configured max columns (${limits.maxColumns}).`,
        );
      }
      field = "";
      const nonEmpty = row.some((x) => String(x ?? "").trim() !== "");
      if (nonEmpty) {
        out.push(row);
        if (Number.isFinite(limits.maxRows) && out.length - 1 > limits.maxRows) {
          throw new Error(
            `Import aborted: CSV exceeds configured max rows (${limits.maxRows}).`,
          );
        }
      }
      row = [];
      continue;
    }

    field += ch;
    if (Number.isFinite(limits.maxCellChars) && field.length > limits.maxCellChars) {
      throw new Error(
        `Import aborted: CSV cell exceeds configured max length (${limits.maxCellChars}).`,
      );
    }
  }

  if (inQuotes) throw new Error("Import aborted: malformed CSV (unterminated quoted field).");

  // Flush last field/row
  row.push(field.trim());
  if (Number.isFinite(limits.maxColumns) && row.length > limits.maxColumns) {
    throw new Error(
      `Import aborted: CSV row exceeds configured max columns (${limits.maxColumns}).`,
    );
  }
  const nonEmpty = row.some((x) => String(x ?? "").trim() !== "");
  if (nonEmpty) {
    out.push(row);
    if (Number.isFinite(limits.maxRows) && out.length - 1 > limits.maxRows) {
      throw new Error(
        `Import aborted: CSV exceeds configured max rows (${limits.maxRows}).`,
      );
    }
  }

  if (out.length < 2) return { header: [], rows: [] };
  const header = out[0];
  const rows = out.slice(1);
  return { header, rows };
}

function normalizeImportMap(spec) {
  const m = spec?.import && typeof spec.import === "object" ? spec.import : {};
  const out = {};
  Object.keys(m).forEach((k) => {
    const arr = Array.isArray(m[k]) ? m[k] : [m[k]];
    out[k] = arr.map((s) => String(s || "").trim().toLowerCase()).filter(Boolean);
  });
  return out;
}

function buildHeaderIndexLookup(headerLower) {
  const map = new Map();
  for (let i = 0; i < headerLower.length; i += 1) {
    const key = String(headerLower[i] || "").trim().toLowerCase();
    if (!key || map.has(key)) continue;
    map.set(key, i);
  }
  return map;
}

function findAliasIndex(headerIndex, aliases) {
  if (!aliases) return -1;
  const arr = Array.isArray(aliases) ? aliases : [aliases];
  for (const alias of arr) {
    const key = String(alias ?? "").trim().toLowerCase();
    if (!key) continue;
    const idx = headerIndex.get(key);
    if (Number.isInteger(idx) && idx >= 0) return idx;
  }
  return -1;
}

function buildCsvImportPlan(spec, importMap, headerLower) {
  const headerIndex = buildHeaderIndexLookup(headerLower);
  const mappedExtractors = [];

  Object.keys(importMap).forEach((key) => {
    const idx = findAliasIndex(headerIndex, importMap[key]);
    if (idx < 0) return;
    mappedExtractors.push({ key, idx });
  });

  const inputs = getColumns(spec, "input").map((col) => ({
    key: String(col?.key || ""),
    directIdx: findAliasIndex(headerIndex, [String(col?.key || "").toLowerCase()]),
    isNumber: col?.type === "number",
  }));

  return { mappedExtractors, inputs };
}

function valuePresent(value) {
  return value != null && String(value).trim() !== "";
}

function buildImportRowFromCsvParts(parts, plan) {
  const obj = {};

  for (const ex of plan.mappedExtractors) {
    obj[ex.key] = ex.idx < parts.length ? parts[ex.idx] : "";
  }

  for (const input of plan.inputs) {
    if (!input.key || input.directIdx < 0) continue;
    if (valuePresent(obj[input.key])) continue;
    const v = input.directIdx < parts.length ? parts[input.directIdx] : "";
    if (v !== "") obj[input.key] = v;
  }

  for (const input of plan.inputs) {
    if (!input.isNumber || !valuePresent(obj[input.key])) continue;
    const s = String(obj[input.key]).trim();
    if (!s) continue;
    const n = safeNumber(s, NaN);
    obj[input.key] = Number.isFinite(n) ? n : obj[input.key];
  }

  return obj;
}

function shouldUseImportWorker(opts = {}, csvText = "") {
  if (opts.importWorker === false) return false;
  if (!canUseCsvWorker()) return false;
  if (opts.importWorker === true) return true;
  const minBytes = normalizeChunkSize(opts.importWorkerMinBytes, 512 * 1024);
  return String(csvText || "").length >= minBytes;
}

function parseCsvInWorker(csvText, limits = {}, { timeoutMs = 60000 } = {}) {
  return new Promise((resolve, reject) => {
    const workerSource = `
self.onmessage = function (ev) {
  const payload = ev && ev.data ? ev.data : {};
  const limits = payload.limits && typeof payload.limits === "object" ? payload.limits : {};
  const src = String(payload.text || "")
    .replace(/\\r\\n/g, "\\n")
    .replace(/\\r/g, "\\n")
    .replace(/^\\uFEFF/, "");

  function fail(msg) {
    self.postMessage({ ok: false, error: String(msg || "CSV parse failed.") });
  }

  function maxLimit(value, fallback) {
    if (value == null || value === "") return fallback;
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    if (n <= 0) return Infinity;
    return Math.max(1, Math.floor(n));
  }

  if (!src.trim()) {
    self.postMessage({ ok: true, header: [], rows: [] });
    return;
  }

  const maxBytes = maxLimit(limits.maxBytes, 50 * 1024 * 1024);
  const maxRows = maxLimit(limits.maxRows, 250000);
  const maxColumns = maxLimit(limits.maxColumns, 2048);
  const maxCellChars = maxLimit(limits.maxCellChars, 500000);

  if (Number.isFinite(maxBytes) && src.length > maxBytes * 2) {
    fail("Import aborted: CSV text exceeds configured size limit (" + maxBytes + " bytes).");
    return;
  }

  const out = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
        if (Number.isFinite(maxCellChars) && field.length > maxCellChars) {
          fail("Import aborted: CSV cell exceeds configured max length (" + maxCellChars + ").");
          return;
        }
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === ",") {
      row.push(field.trim());
      if (Number.isFinite(maxColumns) && row.length > maxColumns) {
        fail("Import aborted: CSV row exceeds configured max columns (" + maxColumns + ").");
        return;
      }
      field = "";
      continue;
    }

    if (ch === "\\n") {
      row.push(field.trim());
      if (Number.isFinite(maxColumns) && row.length > maxColumns) {
        fail("Import aborted: CSV row exceeds configured max columns (" + maxColumns + ").");
        return;
      }
      field = "";
      let nonEmpty = false;
      for (let j = 0; j < row.length; j += 1) {
        if (String(row[j] == null ? "" : row[j]).trim() !== "") {
          nonEmpty = true;
          break;
        }
      }
      if (nonEmpty) {
        out.push(row);
        if (Number.isFinite(maxRows) && out.length - 1 > maxRows) {
          fail("Import aborted: CSV exceeds configured max rows (" + maxRows + ").");
          return;
        }
      }
      row = [];
      continue;
    }

    field += ch;
    if (Number.isFinite(maxCellChars) && field.length > maxCellChars) {
      fail("Import aborted: CSV cell exceeds configured max length (" + maxCellChars + ").");
      return;
    }
  }

  if (inQuotes) {
    fail("Import aborted: malformed CSV (unterminated quoted field).");
    return;
  }

  row.push(field.trim());
  if (Number.isFinite(maxColumns) && row.length > maxColumns) {
    fail("Import aborted: CSV row exceeds configured max columns (" + maxColumns + ").");
    return;
  }
  let nonEmpty = false;
  for (let j = 0; j < row.length; j += 1) {
    if (String(row[j] == null ? "" : row[j]).trim() !== "") {
      nonEmpty = true;
      break;
    }
  }
  if (nonEmpty) {
    out.push(row);
    if (Number.isFinite(maxRows) && out.length - 1 > maxRows) {
      fail("Import aborted: CSV exceeds configured max rows (" + maxRows + ").");
      return;
    }
  }

  if (out.length < 2) {
    self.postMessage({ ok: true, header: [], rows: [] });
    return;
  }

  self.postMessage({
    ok: true,
    header: out[0],
    rows: out.slice(1),
  });
};`;

    const blob = new Blob([workerSource], { type: "application/javascript" });
    const workerUrl = URL.createObjectURL(blob);
    let worker = null;
    let timeoutId = null;
    const cleanup = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (worker) {
        try {
          worker.terminate();
        } catch {}
        worker = null;
      }
      URL.revokeObjectURL(workerUrl);
    };

    try {
      worker = new Worker(workerUrl);
    } catch (err) {
      cleanup();
      reject(err);
      return;
    }

    worker.onmessage = (event) => {
      const data = event?.data || {};
      cleanup();
      if (!data?.ok) {
        reject(new Error(String(data?.error || "CSV parse worker failed.")));
        return;
      }
      resolve({
        header: Array.isArray(data.header) ? data.header : [],
        rows: Array.isArray(data.rows) ? data.rows : [],
      });
    };
    worker.onerror = (event) => {
      cleanup();
      reject(event?.error || new Error(event?.message || "CSV parse worker failed."));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`CSV parse worker timed out after ${timeoutMs}ms.`));
    }, Math.max(1000, Number(timeoutMs) || 60000));

    worker.postMessage({ text: String(csvText || ""), limits });
  });
}

function getColumns(spec, kind /* "input"|"output"|null */) {
  const cols = Array.isArray(spec?.columns) ? spec.columns : [];
  return kind
    ? cols.filter((c) => c && c.kind === kind && c.key)
    : cols.filter((c) => c && c.key);
}

function readOutputRaw(eng, tr, key) {
  if (eng?.getOutputRaw) {
    const v = eng.getOutputRaw(tr, key, true);
    if (Number.isFinite(v)) return v;
  }
  const el = tr.querySelector?.(`[data-out="${key}"]`);
  const raw = el?.dataset?.raw;
  if (raw != null && String(raw).trim() !== "") {
    const n = parseNumber(raw, NaN);
    return Number.isFinite(n) ? n : raw;
  }
  const txt = String(el?.textContent || "").trim();
  const n = parseNumber(txt, NaN);
  return Number.isFinite(n) ? n : txt;
}

function sanitizeCsvFilename(filename, fallback = "table_export.csv") {
  const base = String(filename || "")
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[<>:"/\\|?*]+/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\.+/, "");
  const safe = base || fallback;
  return /\.csv$/i.test(safe) ? safe : `${safe}.csv`;
}

function defaultDownloadCsv(filename, csvText) {
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = sanitizeCsvFilename(filename, "table_export.csv");
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

// -------------------------
// Import validation
// -------------------------
function getImportValidation(spec, opts) {
  const io = spec?.io && typeof spec.io === "object" ? spec.io : null;
  const v = (opts && opts.importValidation) || (io && io.importValidation) || {};
  const mode = String(v.mode || "strict").toLowerCase(); // strict|warn|off
  return {
    mode: mode === "off" ? "off" : mode === "warn" ? "warn" : "strict",
    requiredKeys: Array.isArray(v.requiredKeys) ? v.requiredKeys.map(String) : [],
    minMatched: Number.isFinite(v.minMatched) ? Math.max(0, v.minMatched) : 1,
  };
}

function validateCsvAgainstSpec(spec, headerLower, importMap, validation) {
  if (validation.mode === "off") return { ok: true, matchedKeys: [] };

  const headerSet = new Set(headerLower.map((h) => String(h || "").trim().toLowerCase()));
  const inputs = getColumns(spec, "input");
  const matched = [];

  for (const c of inputs) {
    const key = String(c.key || "");
    const aliases = [key.toLowerCase(), ...(Array.isArray(importMap[key]) ? importMap[key] : [])];
    const has = aliases.some((a) => headerSet.has(String(a || "").trim().toLowerCase()));
    if (has) matched.push(key);
  }

  if (matched.length < validation.minMatched) {
    return {
      ok: false,
      error:
        `Import aborted: file headers do not match this table.\n` +
        `Matched columns: ${matched.length} (need ≥ ${validation.minMatched}).\n` +
        `Headers found: ${headerLower.join(", ")}`,
    };
  }

  const missingReq = (validation.requiredKeys || []).filter((k) => !matched.includes(k));
  if (missingReq.length) {
    return {
      ok: false,
      error:
        `Import aborted: missing required columns: ${missingReq.join(", ")}.\n` +
        `Headers found: ${headerLower.join(", ")}`,
    };
  }

  return { ok: true, matchedKeys: matched };
}

function validateJsonAgainstSpec(spec, items, validation) {
  if (validation.mode === "off") return { ok: true };

  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) return { ok: false, error: "Import aborted: JSON contains no rows/items." };

  const first = arr.find((x) => x && typeof x === "object");
  if (!first) return { ok: false, error: "Import aborted: JSON items are not objects." };

  const keysLower = Object.keys(first).map((k) => String(k).trim().toLowerCase());

  const inputs = getColumns(spec, "input").map((c) => String(c.key || ""));
  const matched = inputs.filter((k) => keysLower.includes(k.toLowerCase()));

  if (matched.length < validation.minMatched) {
    return {
      ok: false,
      error:
        `Import aborted: JSON object keys do not match this table.\n` +
        `Matched columns: ${matched.length} (need ≥ ${validation.minMatched}).\n` +
        `Keys found: ${keysLower.join(", ")}`,
    };
  }

  const missingReq = (validation.requiredKeys || []).filter(
    (k) => !keysLower.includes(String(k).toLowerCase())
  );
  if (missingReq.length) {
    return {
      ok: false,
      error:
        `Import aborted: missing required keys in JSON: ${missingReq.join(", ")}.\n` +
        `Keys found: ${keysLower.join(", ")}`,
    };
  }

  return { ok: true };
}

function normalizeImportMode(mode) {
  const s = String(mode || "").toLowerCase();
  if (s === "append") return "append";
  if (s === "merge") return "merge";
  return "replace";
}

function normalizeConflictAction(action, fallback = "replace") {
  const f = String(fallback || "replace").toLowerCase();
  const raw = String(action == null ? "" : action).trim().toLowerCase();
  if (!raw) return f;
  if (raw === "replace" || raw === "overwrite" || raw === "update") return "replace";
  if (raw === "merge") return "merge";
  if (raw === "keep" || raw === "retain") return "keep";
  if (raw === "skip" || raw === "ignore") return "skip";
  if (raw === "cancel" || raw === "abort" || raw === "stop") return "cancel";
  return f;
}

function normalizeConflictDecision(decision, fallbackAction) {
  if (typeof decision === "string") {
    return {
      action: normalizeConflictAction(decision, fallbackAction),
      applyToAll: false,
    };
  }
  if (!decision || typeof decision !== "object") return null;
  const action = normalizeConflictAction(
    decision.action != null ? decision.action : decision.choice,
    fallbackAction
  );
  return {
    action,
    applyToAll: decision.applyToAll === true,
  };
}

function parseConflictChoice(rawChoice, fallbackAction) {
  let s = String(rawChoice == null ? "" : rawChoice).trim().toLowerCase();
  let applyToAll = false;
  if (s.startsWith("!")) {
    applyToAll = true;
    s = s.slice(1).trim();
  }
  return {
    action: normalizeConflictAction(s, fallbackAction),
    applyToAll,
  };
}

function defaultImportConflictAssistant(conflict) {
  const w = typeof window !== "undefined" ? window : null;
  if (!w || typeof w.prompt !== "function") return null;

  const fallback = normalizeConflictAction(conflict?.defaultAction, "replace");
  const keyName = String(conflict?.conflictKey || "key");
  const keyValue = String(conflict?.keyValue || "");
  const message =
    `Import conflict for ${keyName}="${keyValue}".\n` +
    `Existing row and incoming row share the same key.\n` +
    `Choose action: replace | merge | keep | skip | cancel\n` +
    `Prefix with "!" to apply to all remaining conflicts (example: !replace).`;

  let rawChoice = null;
  try {
    rawChoice = w.prompt(message, fallback);
  } catch {
    return null;
  }

  if (rawChoice == null) return { action: "cancel", applyToAll: false };
  return parseConflictChoice(rawChoice, fallback);
}

function readExistingRows(eng) {
  const existing = [];
  try {
    const trs = eng.getRowEls ? eng.getRowEls() : [];
    for (const tr of trs) existing.push((eng.readRow && eng.readRow(tr)) || {});
  } catch {}
  return existing;
}

function clearAllRows(eng) {
  if (typeof eng.clearRows === "function") {
    eng.clearRows();
    return true;
  }
  if (typeof eng.removeAllRows === "function") {
    eng.removeAllRows();
    return true;
  }
  return false;
}

function appendRows(eng, rows) {
  let count = 0;
  for (const row of rows) {
    if (typeof eng.addRow === "function") eng.addRow(row);
    else if (typeof eng.appendRow === "function") eng.appendRow(row);
    else return { ok: false, count, error: "Engine does not support setItems/addRow APIs." };
    count += 1;
  }
  return { ok: true, count };
}

function normalizeConflictKeyValue(row, conflictKey) {
  if (!row || typeof row !== "object") return "";
  const val = row[conflictKey];
  if (val == null) return "";
  const s = String(val).trim();
  return s;
}

function resolveConflictDecision(conflict, cfg) {
  const fallbackAction = normalizeConflictAction(cfg.defaultAction, "replace");

  if (typeof cfg.resolver === "function") {
    try {
      const decision = normalizeConflictDecision(cfg.resolver(conflict), fallbackAction);
      if (decision?.action) return decision;
    } catch {}
  }

  if (typeof cfg.assistant === "function") {
    try {
      const decision = normalizeConflictDecision(cfg.assistant(conflict), fallbackAction);
      if (decision?.action) return decision;
    } catch {}
  }

  return { action: fallbackAction, applyToAll: false };
}

function mergeImportedRows(existingRows, importedRows, cfg) {
  const conflictKey = String(cfg?.conflictKey || "").trim();
  if (!conflictKey) {
    return {
      ok: false,
      count: 0,
      error: `Import mode "${cfg?.mode || "merge"}" requires a conflict key (opts.conflictKey or spec.rowKey).`,
    };
  }

  const out = Array.isArray(existingRows) ? existingRows.slice() : [];
  const incoming = Array.isArray(importedRows) ? importedRows : [];
  const keyIndex = new Map();

  for (let i = 0; i < out.length; i += 1) {
    const k = normalizeConflictKeyValue(out[i], conflictKey);
    if (!k || keyIndex.has(k)) continue;
    keyIndex.set(k, i);
  }

  let stickyDecision = null;
  let count = 0;
  let appended = 0;
  let replaced = 0;
  let merged = 0;
  let skipped = 0;
  let conflicts = 0;

  for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex += 1) {
    const incomingRow = incoming[incomingIndex];
    const k = normalizeConflictKeyValue(incomingRow, conflictKey);
    const existingIndex = k ? keyIndex.get(k) : undefined;

    if (!k || existingIndex == null) {
      out.push(incomingRow);
      if (k && !keyIndex.has(k)) keyIndex.set(k, out.length - 1);
      count += 1;
      appended += 1;
      continue;
    }

    conflicts += 1;
    const existingRow = out[existingIndex];
    const decision =
      stickyDecision ||
      resolveConflictDecision(
        {
          mode: cfg.mode,
          conflictKey,
          keyValue: k,
          existingIndex,
          incomingIndex,
          existingRow,
          incomingRow,
          defaultAction: cfg.defaultAction,
          spec: cfg.spec,
          eng: cfg.eng,
        },
        cfg
      );

    if (decision?.applyToAll === true) stickyDecision = decision;
    const action = normalizeConflictAction(decision?.action, cfg.defaultAction);

    if (action === "cancel") {
      return {
        ok: false,
        count,
        conflicts,
        skipped,
        appended,
        replaced,
        merged,
        error: `Import cancelled due to conflict on ${conflictKey}="${k}".`,
      };
    }

    if (action === "keep" || action === "skip") {
      skipped += 1;
      continue;
    }

    if (action === "merge") {
      const base = existingRow && typeof existingRow === "object" ? existingRow : {};
      const inc = incomingRow && typeof incomingRow === "object" ? incomingRow : {};
      out[existingIndex] = { ...base, ...inc };
      count += 1;
      merged += 1;
      continue;
    }

    out[existingIndex] = incomingRow;
    count += 1;
    replaced += 1;
  }

  return {
    ok: true,
    rows: out,
    count,
    conflicts,
    skipped,
    appended,
    replaced,
    merged,
  };
}

// -------------------------
// Export
// -------------------------
function buildExportConfig(spec, opts = {}) {
  const inputs = getColumns(spec, "input");
  const outputs = opts.includeOutputs ? getColumns(spec, "output") : [];
  const header =
    Array.isArray(opts.header) && opts.header.length
      ? opts.header.slice()
      : [...inputs.map((c) => c.key), ...outputs.map((c) => c.key)];
  const io = spec?.io && typeof spec.io === "object" ? spec.io : null;
  return { inputs, outputs, header, io };
}

function readExportRow(eng, tr, outputs) {
  const base = eng?.readRow ? eng.readRow(tr) : {};
  const row = { ...base };
  if (outputs.length) {
    for (const c of outputs) row[c.key] = readOutputRaw(eng, tr, c.key);
  }
  return row;
}

function shouldKeepExportRow(row, tr, opts, io) {
  if (typeof opts.filterRow === "function") return !!opts.filterRow(row, tr);
  if (typeof io?.filterRow === "function") return !!io.filterRow(row, tr);
  return true;
}

function buildExportRowObject(row, eng, tr, opts, io) {
  if (typeof opts.buildRow === "function") return opts.buildRow(row, eng, tr) || row;
  if (typeof io?.buildExportRow === "function") return io.buildExportRow(row, eng, tr) || row;
  return row;
}

function normalizeChunkSize(raw, fallback = 250) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(1, Math.floor(n));
}

function yieldToMainThread() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function buildCsvFileName(spec, opts = {}) {
  const ts = new Date().toISOString().replace(/[:]/g, "-").replace(/\..*$/, "");
  const fallback = `${opts.fileNamePrefix || spec?.id || "table"}_${ts}.csv`;
  return sanitizeCsvFilename(opts.fileName || fallback, fallback);
}

function canUseCsvWorker() {
  return (
    typeof Worker !== "undefined" &&
    typeof Blob !== "undefined" &&
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function"
  );
}

function shouldUseCsvWorker(opts = {}, rowCount = 0) {
  if (opts.worker === false) return false;
  if (!canUseCsvWorker()) return false;
  if (opts.worker === true) return true;
  const minRows = normalizeChunkSize(opts.workerMinRows, 2000);
  return rowCount >= minRows;
}

function csvOptionsForWorker(csvOptions) {
  const pattern =
    csvOptions?.formulaPattern instanceof RegExp
      ? csvOptions.formulaPattern
      : DEFAULT_CSV_FORMULA_PATTERN;
  return {
    neutralizeFormulas: csvOptions?.neutralizeFormulas !== false,
    formulaPrefix: String(csvOptions?.formulaPrefix ?? "'"),
    stripNullBytes: csvOptions?.stripNullBytes !== false,
    formulaPatternSource: pattern.source,
    formulaPatternFlags: String(pattern.flags || "").replace(/g/g, ""),
  };
}

function exportCsvInWorker(payload, { timeoutMs = 45000 } = {}) {
  return new Promise((resolve, reject) => {
    const workerSource = `
self.onmessage = function (ev) {
  const payload = ev && ev.data ? ev.data : {};
  const header = Array.isArray(payload.header) ? payload.header : [];
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  const opts = payload.csvOptions && typeof payload.csvOptions === "object" ? payload.csvOptions : {};
  const neutralizeFormulas = opts.neutralizeFormulas !== false;
  const formulaPrefix = typeof opts.formulaPrefix === "string" ? opts.formulaPrefix : "'";
  const stripNullBytes = opts.stripNullBytes !== false;
  let formulaPattern = /^[\\\\t\\\\r ]*[=+\\\\-@]/;
  try {
    const src = typeof opts.formulaPatternSource === "string" && opts.formulaPatternSource
      ? opts.formulaPatternSource
      : formulaPattern.source;
    const flags = typeof opts.formulaPatternFlags === "string"
      ? opts.formulaPatternFlags.replace(/g/g, "")
      : "";
    formulaPattern = new RegExp(src, flags);
  } catch {}

  function sanitize(value) {
    if (value == null) return "";
    if (typeof value !== "string") return value;
    let out = value;
    if (stripNullBytes) out = out.replace(/\\0/g, "");
    if (neutralizeFormulas) {
      formulaPattern.lastIndex = 0;
      if (formulaPattern.test(out)) out = formulaPrefix + out;
    }
    return out;
  }

  function escapeCsv(value) {
    const safe = sanitize(value);
    const s = String(safe == null ? "" : safe);
    if (/[",\\n\\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  }

  const lines = [];
  lines.push(header.map(escapeCsv).join(","));
  for (let i = 0; i < rows.length; i += 1) {
    const row = Array.isArray(rows[i]) ? rows[i] : [];
    lines.push(row.map(escapeCsv).join(","));
  }
  self.postMessage({ ok: true, csvText: lines.join("\\n") });
};`;

    const blob = new Blob([workerSource], { type: "application/javascript" });
    const workerUrl = URL.createObjectURL(blob);
    let worker = null;
    let timeoutId = null;
    const cleanup = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (worker) {
        try {
          worker.terminate();
        } catch {}
        worker = null;
      }
      URL.revokeObjectURL(workerUrl);
    };

    try {
      worker = new Worker(workerUrl);
    } catch (err) {
      cleanup();
      reject(err);
      return;
    }

    worker.onmessage = (event) => {
      cleanup();
      resolve(String(event?.data?.csvText || ""));
    };
    worker.onerror = (event) => {
      cleanup();
      reject(event?.error || new Error(event?.message || "CSV export worker failed."));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`CSV export worker timed out after ${timeoutMs}ms.`));
    }, Math.max(1000, Number(timeoutMs) || 45000));

    worker.postMessage(payload);
  });
}

IO.exportCsvText = function exportCsvText(eng, spec, opts = {}) {
  if (!eng || !spec) return "";

  const { outputs, header, io } = buildExportConfig(spec, opts);
  const csvOptions = resolveCsvOptions(opts);
  const lines = [];
  lines.push(header.map((v) => csvEscape(v, csvOptions)).join(","));

  const rows = eng.getRowEls ? eng.getRowEls() : [];
  for (const tr of rows) {
    const row = readExportRow(eng, tr, outputs);
    if (!shouldKeepExportRow(row, tr, opts, io)) continue;
    const built = buildExportRowObject(row, eng, tr, opts, io);
    lines.push(header.map((k) => csvEscape(built[k], csvOptions)).join(","));
  }

  return lines.join("\n");
};

IO.exportCsvTextAsync = async function exportCsvTextAsync(eng, spec, opts = {}) {
  if (!eng || !spec) return "";

  const rows = eng.getRowEls ? eng.getRowEls() : [];
  const { outputs, header, io } = buildExportConfig(spec, opts);
  const csvOptions = resolveCsvOptions(opts);

  if (shouldUseCsvWorker(opts, rows.length)) {
    const payloadRows = [];
    for (const tr of rows) {
      const row = readExportRow(eng, tr, outputs);
      if (!shouldKeepExportRow(row, tr, opts, io)) continue;
      const built = buildExportRowObject(row, eng, tr, opts, io);
      payloadRows.push(header.map((k) => built[k]));
    }
    try {
      return await exportCsvInWorker(
        {
          header,
          rows: payloadRows,
          csvOptions: csvOptionsForWorker(csvOptions),
        },
        { timeoutMs: opts.workerTimeoutMs },
      );
    } catch (err) {
      if (opts.worker === true || opts.throwWorkerError === true) throw err;
    }
  }

  const lines = [];
  lines.push(header.map((v) => csvEscape(v, csvOptions)).join(","));
  const chunkSize = normalizeChunkSize(opts.chunkSize, 250);

  for (let i = 0; i < rows.length; i += 1) {
    const tr = rows[i];
    const row = readExportRow(eng, tr, outputs);
    if (!shouldKeepExportRow(row, tr, opts, io)) continue;
    const built = buildExportRowObject(row, eng, tr, opts, io);
    lines.push(header.map((k) => csvEscape(built[k], csvOptions)).join(","));
    if ((i + 1) % chunkSize === 0) await yieldToMainThread();
  }

  return lines.join("\n");
};

IO.downloadCsv = function downloadCsv(eng, spec, opts = {}) {
  const csvText = IO.exportCsvText(eng, spec, opts);
  if (!csvText) return;
  defaultDownloadCsv(buildCsvFileName(spec, opts), csvText);
};

IO.downloadCsvAsync = async function downloadCsvAsync(eng, spec, opts = {}) {
  const csvText = await IO.exportCsvTextAsync(eng, spec, opts);
  if (!csvText) return;
  defaultDownloadCsv(buildCsvFileName(spec, opts), csvText);
};

IO.exportJson = function exportJson(eng, spec, opts = {}) {
  if (!eng || !spec) return [];
  const { inputs, outputs, io } = buildExportConfig(spec, opts);

  const rows = eng.getRowEls ? eng.getRowEls() : [];
  const out = [];

  for (const tr of rows) {
    const row = readExportRow(eng, tr, outputs);
    if (!shouldKeepExportRow(row, tr, opts, io)) continue;
    const built = buildExportRowObject(row, eng, tr, opts, io);

    const min = {};
    for (const c of inputs) min[c.key] = built[c.key];
    for (const c of outputs) min[c.key] = built[c.key];
    out.push({ ...min, ...built });
  }

  return out;
};

// -------------------------
// Import (CSV/JSON)
// -------------------------
IO.importItems = function importItems(eng, spec, items, opts = {}) {
  if (!eng || !spec) return { ok: false, count: 0, error: "Missing engine/spec." };

  const io = spec.io && typeof spec.io === "object" ? spec.io : null;
  const mode = normalizeImportMode(opts.mode);
  const limits = getImportLimits(spec, opts);

  const src = Array.isArray(items) ? items : [];
  if (Number.isFinite(limits.maxRows) && src.length > limits.maxRows) {
    return {
      ok: false,
      count: 0,
      error: `Import aborted: row count exceeds configured max (${limits.maxRows}).`,
    };
  }
  const builtRows = [];

  for (const it of src) {
    let row = it && typeof it === "object" ? { ...it } : null;
    if (!row) continue;

    if (typeof opts.sanitizeRow === "function") row = opts.sanitizeRow(row) || row;
    else if (typeof io?.sanitizeRow === "function") row = io.sanitizeRow(row) || row;

    if (typeof opts.buildRow === "function") row = opts.buildRow(row) || row;
    else if (typeof io?.buildImportRow === "function") row = io.buildImportRow(row) || row;

    builtRows.push(row);
  }

  const rawConflictAction =
    opts.conflictAction !== undefined ? opts.conflictAction : io?.importConflictAction;
  const defaultConflictAction = normalizeConflictAction(rawConflictAction, "replace");
  const conflictKey = String(
    opts.conflictKey !== undefined ? opts.conflictKey : io?.importConflictKey || spec.rowKey || ""
  ).trim();
  const resolveConflict =
    typeof opts.resolveConflict === "function"
      ? opts.resolveConflict
      : typeof io?.resolveImportConflict === "function"
        ? io.resolveImportConflict
        : typeof io?.importConflictResolver === "function"
          ? io.importConflictResolver
          : null;
  const conflictAssistantSource =
    opts.conflictAssistant !== undefined ? opts.conflictAssistant : io?.importConflictAssistant;
  const conflictAssistant =
    typeof conflictAssistantSource === "function"
      ? conflictAssistantSource
      : conflictAssistantSource === true
        ? defaultImportConflictAssistant
        : null;
  const conflictFlowEnabled =
    mode === "merge" ||
    rawConflictAction != null ||
    typeof resolveConflict === "function" ||
    typeof conflictAssistant === "function";

  if (conflictFlowEnabled && !conflictKey) {
    return {
      ok: false,
      count: 0,
      error: `Import mode "${mode}" requires a conflict key (opts.conflictKey or spec.rowKey).`,
    };
  }

  // Prefer setItems (most deterministic / fastest in your app)
  if (typeof eng.setItems === "function") {
    if (mode === "replace") {
      eng.setItems(builtRows);
      return { ok: true, count: builtRows.length };
    }

    if (!conflictFlowEnabled && mode === "append") {
      const existing = readExistingRows(eng);
      eng.setItems([...existing, ...builtRows]);
      return { ok: true, count: builtRows.length };
    }

    const existing = readExistingRows(eng);
    const merged = mergeImportedRows(existing, builtRows, {
      mode,
      conflictKey,
      defaultAction: defaultConflictAction,
      resolver: resolveConflict,
      assistant: conflictAssistant,
      spec,
      eng,
    });
    if (!merged.ok) return merged;

    eng.setItems(merged.rows);
    return {
      ok: true,
      count: merged.count,
      conflicts: merged.conflicts,
      skipped: merged.skipped,
      appended: merged.appended,
      replaced: merged.replaced,
      merged: merged.merged,
    };
  }

  // Fallback to row APIs if present
  if (mode === "replace") {
    if (!clearAllRows(eng)) {
      return { ok: false, count: 0, error: "Engine does not support setItems/clearRows APIs." };
    }
    return appendRows(eng, builtRows);
  }

  if (!conflictFlowEnabled && mode === "append") {
    return appendRows(eng, builtRows);
  }

  const existing = readExistingRows(eng);
  const merged = mergeImportedRows(existing, builtRows, {
    mode,
    conflictKey,
    defaultAction: defaultConflictAction,
    resolver: resolveConflict,
    assistant: conflictAssistant,
    spec,
    eng,
  });
  if (!merged.ok) return merged;

  if (!clearAllRows(eng)) {
    return {
      ok: false,
      count: 0,
      error: "Engine does not support clearing rows for merge import.",
    };
  }

  const appendedRows = appendRows(eng, merged.rows);
  if (!appendedRows.ok) return appendedRows;
  return {
    ok: true,
    count: merged.count,
    conflicts: merged.conflicts,
    skipped: merged.skipped,
    appended: merged.appended,
    replaced: merged.replaced,
    merged: merged.merged,
  };
};

IO.importCsvText = function importCsvText(eng, spec, csvText, opts = {}) {
  const importMap = normalizeImportMap(spec);
  const limits = getImportLimits(spec, opts);

  let parsed;
  try {
    parsed = parseCSV(csvText, limits);
  } catch (err) {
    return { ok: false, count: 0, error: String(err?.message || err) };
  }
  const { header, rows } = parsed;
  const headerLower = header.map((h) => String(h || "").trim().toLowerCase());

  const validation = getImportValidation(spec, opts);
  const vres = validateCsvAgainstSpec(spec, headerLower, importMap, validation);
  if (!vres.ok) return { ok: false, count: 0, error: vres.error };

  const plan = buildCsvImportPlan(spec, importMap, headerLower);
  const items = [];
  for (let i = 0; i < rows.length; i += 1) {
    items.push(buildImportRowFromCsvParts(rows[i], plan));
  }

  return IO.importItems(eng, spec, items, opts);
};

IO.importCsvTextAsync = async function importCsvTextAsync(eng, spec, csvText, opts = {}) {
  const importMap = normalizeImportMap(spec);
  const limits = getImportLimits(spec, opts);

  let parsed;
  if (shouldUseImportWorker(opts, csvText)) {
    try {
      parsed = await parseCsvInWorker(csvText, limits, {
        timeoutMs: opts.importWorkerTimeoutMs,
      });
    } catch (err) {
      if (opts.importWorker === true || opts.throwWorkerError === true) {
        return { ok: false, count: 0, error: String(err?.message || err) };
      }
    }
  }
  if (!parsed) {
    try {
      parsed = parseCSV(csvText, limits);
    } catch (err) {
      return { ok: false, count: 0, error: String(err?.message || err) };
    }
  }

  const { header, rows } = parsed;
  const headerLower = header.map((h) => String(h || "").trim().toLowerCase());

  const validation = getImportValidation(spec, opts);
  const vres = validateCsvAgainstSpec(spec, headerLower, importMap, validation);
  if (!vres.ok) return { ok: false, count: 0, error: vres.error };

  const plan = buildCsvImportPlan(spec, importMap, headerLower);
  const chunkSize = normalizeChunkSize(opts.importChunkSize, 1000);
  const items = new Array(rows.length);

  for (let i = 0; i < rows.length; i += 1) {
    items[i] = buildImportRowFromCsvParts(rows[i], plan);
    if ((i + 1) % chunkSize === 0) {
      if (typeof opts.onProgress === "function") {
        try {
          opts.onProgress({
            phase: "parse_csv_rows",
            completed: i + 1,
            total: rows.length,
          });
        } catch {}
      }
      await yieldToMainThread();
    }
  }

  return IO.importItems(eng, spec, items, opts);
};

IO.importJsonText = function importJsonText(eng, spec, jsonText, opts = {}) {
  const limits = getImportLimits(spec, opts);
  if (Number.isFinite(limits.maxBytes) && String(jsonText || "").length > limits.maxBytes * 2) {
    return {
      ok: false,
      count: 0,
      error: `Import aborted: JSON exceeds configured size limit (${limits.maxBytes} bytes).`,
    };
  }

  let obj;
  try {
    obj = JSON.parse(jsonText);
  } catch (err) {
    return { ok: false, count: 0, error: `Import aborted: malformed JSON (${err?.message || err}).` };
  }
  const items = Array.isArray(obj)
    ? obj
    : Array.isArray(obj?.items)
      ? obj.items
      : Array.isArray(obj?.data)
        ? obj.data
        : [];

  if (Number.isFinite(limits.maxRows) && items.length > limits.maxRows) {
    return {
      ok: false,
      count: 0,
      error: `Import aborted: row count exceeds configured max (${limits.maxRows}).`,
    };
  }

  const validation = getImportValidation(spec, opts);
  const vres = validateJsonAgainstSpec(spec, items, validation);
  if (!vres.ok) return { ok: false, count: 0, error: vres.error };

  return IO.importItems(eng, spec, items, opts);
};

IO.importFile = async function importFile(eng, spec, file, opts = {}) {
  const name = String(file?.name || "").toLowerCase();
  const limits = getImportLimits(spec, opts);
  if (Number.isFinite(limits.maxBytes) && Number(file?.size || 0) > limits.maxBytes) {
    return {
      ok: false,
      count: 0,
      error: `Import aborted: file exceeds configured size limit (${limits.maxBytes} bytes).`,
    };
  }
  const text = await file.text();

  if (name.endsWith(".json")) return IO.importJsonText(eng, spec, text, opts);
  return IO.importCsvTextAsync(eng, spec, text, opts);
};

export const TableEngineIO = Object.freeze(IO);
export default TableEngineIO;
